import React, { useContext, useState, useEffect, useMemo } from 'react'
import { Redirect, useHistory } from 'react-router-dom'
import { needsNoProjectPages, needsNoCapTablePages } from 'router'
import { notification } from 'antd'
import { Helmet } from 'react-helmet'
import { apiContext } from 'services/ApiContext'
import { useLoadingType, useTimedOut } from 'services/utils'
import PageNotice from 'components/GeneralComponents/PageNotice'
import MigrationNotice from 'components/GeneralComponents/MigrationNotice'
import PageErrorBoundary from 'components/ErrorComponents/PageErrorBoundary'
import { Fade } from 'components/GeneralComponents/Animations'
import { restrictions } from 'utils/enums'
import ErrorPage from 'pages/Minimal/ErrorPage'
import {
  AGB_NEEDS_AGREEMENT,
  GRUENDERPLATFORM_ACCOUNT_CREATION_URL,
  GRUENDERPLATFORM_MIGRATION,
} from 'utils/constants'
import AGBNotifier from 'components/GeneralComponents/AGBNotifier'
import axios from 'axios'
import sessionStorage from 'store'

import Loading from './Loading'
import Paywall from '../Paywall'

const { NO_AUTH, NO_PREMIUM, NO_GROUP_OWNER, NO_GROUP } = restrictions

// Checks if user is permissioned for page and otherwise redirects
const Authorize = ({
  children,
  title,
  redirect = false,
  to = '/404',
  sampleImage,
  projectRoles = [],
  fadeIn = true,
  restrictAccess = () => null,
}) => {
  const { user, membership, project, company, rounds } = useContext(apiContext)
  const { push, location } = useHistory()
  const loadingType = useLoadingType()
  const [animationRunning, setAnimationRunning] = useState(loadingType === 'AUTHENTICATE')
  // Ensure to show user feedback if no server response (30sec)
  const { timedOut, clearTimer } = useTimedOut(30000, loadingType === 'DONE')

  // Ensure with flag that if shown, then animation should be shown at least 4sec (even if loading is faster)
  useEffect(() => {
    if (animationRunning) setTimeout(() => setAnimationRunning(false), 4000)
    if (loadingType === 'DONE') clearTimer()
  }, [loadingType])

  // useMemo => Avoid rerender on change of auth0 (background syncing)
  return useMemo(() => {
    if (timedOut)
      return <ErrorPage errorId="errorPage.timeout" buttonFunc={() => window.location.reload()} />

    if (!(loadingType === 'DONE') || animationRunning)
      return <Loading loadingType={loadingType} animation={loadingType === 'AUTHENTICATE'} />

    if (AGB_NEEDS_AGREEMENT && (!user || !user.agreedLatestAGB)) {
      return <AGBNotifier />
    }
    if (
      GRUENDERPLATFORM_MIGRATION &&
      user &&
      !sessionStorage.get('cancelled_user_migration_gruenderplatform', false)
    ) {
      const migrateUser = () => {
        axios
          .post(
            GRUENDERPLATFORM_ACCOUNT_CREATION_URL,
            {
              // "first_name": user.firstname,
              // "last_name": user.lastname,
              email: user.email,
              user_id: user.userId,
              // "company": user.business ? user.business.name : null,
              // "street":  null,
              // "zip": null,
              // "city": null,
              // "country": null,
            },
            {
              headers: {},
              auth: {
                username: 'test@individee.com',
                password: 'fY9HdKkyGEZGcHlVnkQO6zO3lyFELPbpMYIu',
              },
            },
          )
          .then((response) => {
            if (response.data.success) {
              window.location.href = response.data.target_url
            } else {
              notification.error({
                message: 'Unsuccessful login',
                description:
                  'The login in the new infrastructure failed, please contact our support!',
              })
              console.log('Error logging, debug:', response.data.errors)
            }
          })
      }

      const objectMigration = () => {
        sessionStorage.set('cancelled_user_migration_gruenderplatform', true)
        window.location.reload()
      }

      return (
        <MigrationNotice
          id="migrateUserInfo"
          idText="migrateUserText"
          className=""
          action="migrateUser"
          onClick={() => migrateUser()}
          cancel="cancelMigration"
          cancelOnClick={() => objectMigration()}
        />
      )
    }

    // No membership - invalid project (force user to edit)
    const isNeedsNoProject = needsNoProjectPages.some((p) => location.pathname.includes(p))
    const isNeedsNoCapTable = needsNoCapTablePages.some((p) => location.pathname.includes(p))
    if (user.group && !project && !isNeedsNoProject) {
      return (
        <PageNotice
          id="noProjectsButGroup"
          action="joinProjects"
          onClick={() => push('/portfolio')}
        />
      )
    }
    if (!project && !isNeedsNoProject) {
      return <PageNotice id="noProjects" action="createProject" onClick={() => push('/guide')} />
    }
    if (!company && !isNeedsNoCapTable) {
      return <PageNotice id="noCharts" action="createCompany" onClick={() => push('/edit')} />
    }
    if (rounds.length < 1 && !isNeedsNoCapTable) {
      return (
        <PageNotice id="noCharts" action="createFinancingRound" onClick={() => push('/edit')} />
      )
    }

    const restriction = restrictAccess(user, project)
    const authRestriction = [NO_AUTH, NO_GROUP_OWNER, NO_GROUP].includes(restriction)

    // Auth restriction or no project rights (if project required)
    const authorized =
      (!project && isNeedsNoProject) ||
      (!authRestriction && membership && projectRoles.includes(membership.projectRole))

    const AuthorizedChildren = () => {
      // if user not equal needed role and if component is a page - make redirect to needed route
      if (!authorized && redirect) {
        notification.error({
          message: 'Unauthorized Access',
          description: 'You have no rights to access this page!',
        })
        return <Redirect to={to} />
      }
      // if user not authorized return null to component
      if (!authorized) {
        return null
      }

      // if page requires premium access and user doesn't have it show paywall
      if (restriction === NO_PREMIUM) {
        return (
          <>
            <Paywall sampleImage={sampleImage}>
              <PageErrorBoundary>
                <Fade in disabled={!fadeIn}>
                  {children}
                </Fade>
              </PageErrorBoundary>
            </Paywall>
          </>
        )
      }

      // if access is successful render children
      return (
        <>
          <Helmet title={title} />
          <PageErrorBoundary>
            <Fade in disabled={!fadeIn}>
              {children}
            </Fade>
          </PageErrorBoundary>
        </>
      )
    }
    return AuthorizedChildren()
  }, [user, membership, redirect, children, title, to, animationRunning, restrictAccess, timedOut])
}

export default Authorize
